import { CLIENTPORTAL_CAPABILITY_V2 as definitions } from '../clientportal-capability-contract'
import { CLIENTPORTAL_DEFAULT_THEME_V2 } from '../theme-contracts'
import { serializeJsonV2 } from '../serialize-json-v2'
import { nodeV2, readyStateV2 } from './default-builders'
import type { ExperienceDefaultsV2, ExperienceNodeV2 } from 'sdui-core'

export const CLIENTPORTAL_WEB_BASE_VERSION = 1

const gridConfig = (gap: 'none' | 'sm' | 'md' | 'lg' = 'none') => ({ columns: { compact: 4, web: 12 }, gap })
const itemConfig = (web: number = 12, order: number = 1) => ({
  span: { compact: 4, web },
  order: { compact: order, web: order },
  wrapper: 'box',
})

interface GridItemInput {
  readonly id: string
  readonly capability: string
  readonly web?: number
  readonly config?: Record<string, unknown>
  readonly requires?: readonly string[]
}

const gridScreen = (slug: string, items: readonly GridItemInput[], gap: 'none' | 'sm' | 'md' | 'lg' = 'lg') => {
  const root = `${slug}-grid`
  const elements: Record<string, ExperienceNodeV2> = {
    [root]: nodeV2(definitions, 'web.layout.grid', {
      children: items.map(item => `${item.id}-layout`),
      config: gridConfig(gap),
    }),
  }
  items.forEach((item, index) => {
    elements[`${item.id}-layout`] = nodeV2(definitions, 'web.layout.grid-item', {
      children: [item.id],
      config: itemConfig(item.web ?? 12, index + 1),
      requires: item.requires,
    })
    elements[item.id] = nodeV2(definitions, item.capability, { config: item.config })
  })
  return readyStateV2(root, elements)
}

export const CLIENTPORTAL_WEB_DEFAULTS_V2 = {
  contractVersion: 2,
  baseVersion: CLIENTPORTAL_WEB_BASE_VERSION,
  artifact: 'experience-defaults',
  product: 'clientportal',
  platform: 'web',
  scope: 'location',
  theme: structuredClone(CLIENTPORTAL_DEFAULT_THEME_V2),
  navigation: {
    order: ['events'],
    hidden: [],
  },
  screens: {
    'clientportal.events': gridScreen('events-hub', [
      {
        id: 'events-header',
        capability: 'clientportal.events.header',
        requires: ['events.access'],
        config: { heading: 'events' },
      },
      { id: 'events-live-now', capability: 'clientportal.events.live-now', requires: ['events.access'] },
      { id: 'events-list', capability: 'clientportal.events.list', requires: ['events.access'] },
    ]),
    'clientportal.events.live': gridScreen('events-live', [
      {
        id: 'events-live-header',
        capability: 'clientportal.events.header',
        requires: ['events.access'],
        config: { heading: 'live' },
      },
      { id: 'events-live-directory', capability: 'clientportal.events.directory', requires: ['events.access'] },
    ]),
    'clientportal.events.upcoming': gridScreen('events-upcoming', [
      {
        id: 'events-upcoming-header',
        capability: 'clientportal.events.header',
        requires: ['events.access'],
        config: { heading: 'upcoming' },
      },
      { id: 'events-upcoming-directory', capability: 'clientportal.events.directory', requires: ['events.access'] },
    ]),
    'clientportal.events.past': gridScreen('events-past', [
      {
        id: 'events-past-header',
        capability: 'clientportal.events.header',
        requires: ['events.access'],
        config: { heading: 'past' },
      },
      { id: 'events-past-directory', capability: 'clientportal.events.directory', requires: ['events.access'] },
    ]),
    'clientportal.events.detail': gridScreen('events-detail', [
      {
        id: 'events-detail',
        capability: 'clientportal.events.detail',
        requires: ['events.access'],
        config: {
          showAbout: true,
          showAgenda: true,
          showSidebar: true,
          showTickets: true,
          showSpeakers: true,
          showAttendees: true,
          showExhibitors: true,
        },
      },
    ]),
  },
} satisfies ExperienceDefaultsV2

export const serializeClientPortalWebDefaultsV2 = () => serializeJsonV2(CLIENTPORTAL_WEB_DEFAULTS_V2)
